import React, { forwardRef } from 'react';
import { Wind, Droplets } from 'lucide-react';
import { resolveWeatherIconCode } from '../../../../util/weatherCodeMapper';
import {
  buildElevationPaths,
  climbCategoryColor,
  formatDistance,
  formatDuration,
  formatElevation,
  formatGrade,
  formatHHMM,
  formatShortDate,
  pickRepresentativeSegments,
  travelModeLabel,
} from './format';

// Loads a weather PNG icon synchronously via require so html-to-image
// can capture it without waiting for async dynamic imports.
function getWeatherIconSrc(weatherCode, period) {
  if (!weatherCode) return null;
  const code = resolveWeatherIconCode(weatherCode, period);
  try {
    // eslint-disable-next-line import/no-dynamic-require
    return require(`../../../../assets/weathericons/${code}_g.png`);
  } catch {
    return null;
  }
}

// Dimensions for each export format.
const DIMS = {
  square: { width: 1080, height: 1080, chartH: 320, padV: 56, padH: 64 },
  story: { width: 1080, height: 1920, chartH: 440, padV: 80, padH: 64 },
};


// A fixed-size social share card, rendered off-screen and exported to PNG via
// html-to-image. Uses only inline styles and hand-built SVG — no Chakra, no
// external fonts at render time (system Roboto / sans-serif fallback).
//
// IMPORTANT: this element must have NO transform of its own. The wrapper div
// in RouteProfile.js carries the translateX(-9999px) that hides it; if this
// element had transform, html-to-image would inline it on the clone and the
// content would render off-canvas → blank image.
const ShareCard = forwardRef(function ShareCard(
  { profile, segments = [], origin, destination, departureTime, travelMode, format = 'square', lang = 'es', weatherRows = [] },
  ref,
) {
  const { width, height, chartH, padV, padH } = DIMS[format] || DIMS.square;
  const innerW = width - padH * 2;

  const samples = profile?.samples || [];
  const stats = profile?.stats || null;

  const repSegments = pickRepresentativeSegments(segments, 5);
  const paths = buildElevationPaths(samples, innerW, chartH, 8);

  // Segment-based X positions (distance-normalized, 0..1), kept as fallback.
  const maxDist = samples.length > 0 ? samples[samples.length - 1]?.dist || 1 : 1;
  const segPositions = repSegments.map((seg) => {
    const dist = seg.cumulativeDist ?? null;
    return dist != null ? dist / maxDist : null;
  });

  const modeLabel = travelModeLabel(travelMode, lang);
  const depLabel = departureTime
    ? `${formatShortDate(departureTime, lang)} · ${formatHHMM(departureTime)}`
    : null;

  // Weather localities with forecast data.
  const weatherWithData = weatherRows.filter((r) => r.hasData);
  const repWeather = pickRepresentativeSegments(weatherWithData, 5);

  // Time-based X position helper (0..1 along total route duration).
  const totalMs = (profile?.totalDurationSec || 1) * 1000;
  const getXFromTime = (t) =>
    departureTime && t
      ? Math.max(0, Math.min(1, (t.getTime() - departureTime.getTime()) / totalMs))
      : null;

  // Use weather-based timeline waypoints when data is available; fall back to segments.
  const useWeatherTimeline = repWeather.length > 0 && !!departureTime;
  const timelinePoints = useWeatherTimeline
    ? repWeather.map((r) => ({
        pos: getXFromTime(r.passingTime),
        time: r.passingTime,
        label: r.name,
        weatherCode: r.weatherCode,
        period: r.period,
        temp: r.temperature,
      }))
    : repSegments.map((seg, i) => ({
        pos: segPositions[i],
        time: seg.arrivalTime,
        label: seg.from || seg.to || '',
        weatherCode: null,
        period: null,
        temp: null,
      }));

  // Colors — white + green theme
  const green = '#38A169';
  const textPrimary = '#1a202c';
  const textSub = '#4a5568';
  const textDim = '#a0aec0';
  const gridColor = '#e2e8f0';

  return (
    <div
      ref={ref}
      style={{
        width,
        height,
        display: 'flex',
        flexDirection: 'column',
        padding: `${padV}px ${padH}px`,
        boxSizing: 'border-box',
        background: 'linear-gradient(160deg, #f7fdf9 0%, #ffffff 100%)',
        fontFamily: '"Roboto", "Helvetica Neue", Arial, sans-serif',
        color: textPrimary,
        overflow: 'hidden',
      }}
    >
      {/* ── Header: brand + travel mode badge ─────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 20, height: 20, background: green, borderRadius: 6 }} />
          <span style={{ fontSize: 30, fontWeight: 700, color: green, letterSpacing: 0.5 }}>
            RouteForecast.app
          </span>
        </div>
        {modeLabel ? (
          <div
            style={{
              background: green,
              color: '#ffffff',
              fontSize: 18,
              fontWeight: 700,
              padding: '6px 18px',
              borderRadius: 99,
              letterSpacing: 1,
              textTransform: 'uppercase',
            }}
          >
            {modeLabel}
          </div>
        ) : null}
      </div>

      {/* ── Title: origin → destination ───────────────────────────── */}
      <div style={{ marginBottom: format === 'story' ? 40 : 24 }}>
        <div
          style={{
            fontSize: format === 'story' ? 64 : 54,
            fontWeight: 700,
            lineHeight: 1.05,
            marginBottom: 8,
            color: textPrimary,
          }}
        >
          {origin || '—'}          <span style={{ fontSize: format === 'story' ? 40 : 36, color: green, fontWeight: 300 }}>
            {destination || '—'}
          </span>
        </div>

      </div>

      {/* ── Elevation sparkline ───────────────────────────────────── */}
      <div style={{ width: '100%' }}>
        {paths ? (
          <svg
            width={innerW}
            height={chartH}
            viewBox={`0 0 ${innerW} ${chartH}`}
            style={{ display: 'block' }}
          >
            <line x1={0} y1={chartH - 1} x2={innerW} y2={chartH - 1} stroke={gridColor} strokeWidth={2} />
            {timelinePoints.map((pt, i) =>
              pt.pos != null ? (
                <line
                  key={i}
                  x1={(pt.pos * innerW).toFixed(1)}
                  y1={0}
                  x2={(pt.pos * innerW).toFixed(1)}
                  y2={chartH}
                  stroke={gridColor}
                  strokeWidth={1}
                  strokeDasharray="6 4"
                />
              ) : null,
            )}
            <path d={paths.area} fill={green} fillOpacity="0.15" />
            <path d={paths.line} fill="none" stroke={green} strokeWidth={3} strokeLinejoin="round" />
          </svg>
        ) : (
          <div
            style={{
              height: chartH,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: textDim,
              fontSize: 24,
            }}
          >
            Perfil de elevacion no disponible
          </div>
        )}
      </div>

      {/* ── Timeline with weather-enriched waypoints ──────────────── */}
      <div
        style={{
          width: innerW,
          display: 'flex',
          flexDirection: 'column',
          marginBottom: format === 'story' ? 36 : 20,
        }}
      >
        {/* Tick marks row */}
        <div style={{ position: 'relative', height: 8 }}>
          {timelinePoints.map((pt, i) =>
            pt.pos != null ? (
              <div
                key={i}
                style={{
                  position: 'absolute',
                  left: `${(pt.pos * 100).toFixed(1)}%`,
                  top: 0,
                  width: 1,
                  height: 8,
                  background: gridColor,
                  transform: 'translateX(-50%)',
                }}
              />
            ) : null,
          )}
        </div>
        {/* Labels row — 80px tall to fit icon+temp+time+name */}
        <div style={{ position: 'relative', height: 80, marginTop: 4 }}>
          {timelinePoints.map((pt, i) => {
            if (pt.pos == null) return null;
            const pct = (pt.pos * 100).toFixed(1);
            const isFirst = i === 0;
            const isLast = i === timelinePoints.length - 1;
            const align = isFirst ? 'flex-start' : isLast ? 'flex-end' : 'center';
            const iconSrc = getWeatherIconSrc(pt.weatherCode, pt.period);
            return (
              <div
                key={i}
                style={{
                  position: 'absolute',
                  left: `${pct}%`,
                  top: 0,
                  transform: isFirst ? 'none' : isLast ? 'translateX(-100%)' : 'translateX(-50%)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: align,
                  whiteSpace: 'nowrap',
                }}
              >
                {iconSrc && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <img src={iconSrc} alt="" style={{ width: 20, height: 20 }} />
                    {pt.temp != null && (
                      <span style={{ fontSize: 15, fontWeight: 700, color: textPrimary }}>
                        {pt.temp}°
                      </span>
                    )}
                  </div>
                )}
                <span style={{ fontSize: format === 'story' ? 20 : 17, fontWeight: 700, color: green }}>
                  {formatHHMM(pt.time)}
                </span>
                <span
                  style={{
                    fontSize: format === 'story' ? 15 : 13,
                    color: textSub,
                    maxWidth: 150,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {pt.label}
                </span>
              </div>
            );
          })}
        </div>
        {timelinePoints.length === 0 && depLabel && (
          <div style={{ fontSize: 18, color: textSub, marginTop: 8 }}>{depLabel}</div>
        )}
      </div>

      {/* ── Forecast table (story only) ───────────────────────────── */}
      {format === 'story' && weatherWithData.length > 0 && (
        <div style={{ marginBottom: 28 }}>
          <div
            style={{
              fontSize: 15,
              color: textDim,
              textTransform: 'uppercase',
              letterSpacing: 1.5,
              marginBottom: 12,
            }}
          >
            Previsión de tiempo
          </div>
          {repWeather.map((row, i) => {
            const rowIconSrc = getWeatherIconSrc(row.weatherCode, row.period);
            return (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '14px 16px',
                  background: i % 2 === 0 ? 'rgba(56,161,105,0.06)' : 'transparent',
                  borderRadius: 8,
                  gap: 16,
                }}
              >
                {rowIconSrc ? (
                  <img src={rowIconSrc} alt="" style={{ width: 34, height: 34, flexShrink: 0 }} />
                ) : (
                  <div style={{ width: 34, height: 34, flexShrink: 0 }} />
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 22, fontWeight: 700, color: green }}>
                    {formatHHMM(row.passingTime)}
                  </div>
                  <div
                    style={{
                      fontSize: 16,
                      color: textSub,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {row.name}
                  </div>
                </div>
                {row.temperature != null && (
                  <div style={{ fontSize: 32, fontWeight: 700, color: textPrimary, flexShrink: 0 }}>
                    {row.temperature}°
                  </div>
                )}
                {row.windSpeed != null && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                    <Wind size={16} color={textSub} strokeWidth={2} />
                    <span style={{ fontSize: 16, color: textSub }}>{row.windSpeed} km/h</span>
                  </div>
                )}
                {row.precipitation != null && row.precipitation > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                    <Droplets size={16} color="#3182CE" strokeWidth={2} />
                    <span style={{ fontSize: 16, color: '#3182CE' }}>{row.precipitation} mm</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Stats ─────────────────────────────────────────────────── */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          background: 'rgba(0,0,0,0.04)',
          borderRadius: 12,
          padding: format === 'story' ? '28px 32px' : '18px 22px',
          marginBottom: format === 'story' ? 28 : 16,
          gap: format === 'story' ? 20 : 14,
        }}
      >
        {/* Row 1: distance, duration, ascent, descent */}
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
          {[
            { label: 'Distancia', value: formatDistance(profile?.totalDistance) },
            { label: 'Duración', value: formatDuration(profile?.totalDurationSec) },
            { label: 'Desnivel +', value: stats ? formatElevation(stats.ascent) : '--', color: '#276749' },
            { label: 'Desnivel -', value: stats ? formatElevation(stats.descent) : '--', color: '#c53030' },
          ].map(({ label, value, color }) => (
            <div key={label} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
              <div style={{ fontSize: format === 'story' ? 15 : 12, color: textDim, textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 5 }}>
                {label}
              </div>
              <div style={{ fontSize: format === 'story' ? 38 : 29, fontWeight: 700, color: color || textPrimary, lineHeight: 1 }}>
                {value}
              </div>
            </div>
          ))}
        </div>

        {/* Row 2: max elevation + max grade */}
        {stats && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-evenly',
              gap: 16,
              borderTop: `1px solid ${gridColor}`,
              paddingTop: format === 'story' ? 20 : 14,
            }}
          >
            {[
              { label: 'Alt. máx', value: formatElevation(stats.maxEle) },
              { label: 'Pendiente máx', value: formatGrade(stats.maxGrade) },
            ].map(({ label, value }) => (
              <div key={label} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                <div style={{ fontSize: format === 'story' ? 15 : 12, color: textDim, textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 5 }}>
                  {label}
                </div>
                <div style={{ fontSize: format === 'story' ? 38 : 29, fontWeight: 700, color: textPrimary, lineHeight: 1 }}>
                  {value}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Climbs (story only) ───────────────────────────────────── */}
      {format === 'story' && stats?.climbs?.length > 0 && (
        <div style={{ marginBottom: 28 }}>
          <div
            style={{
              fontSize: 15,
              color: textDim,
              textTransform: 'uppercase',
              letterSpacing: 1.5,
              marginBottom: 12,
            }}
          >
            Puertos
          </div>
          {stats.climbs.slice(0, 3).map((c, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '12px 0',
                borderBottom: i < Math.min(stats.climbs.length, 3) - 1 ? `1px solid ${gridColor}` : 'none',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div
                  style={{
                    background: climbCategoryColor(c.category),
                    color: '#fff',
                    fontSize: 15,
                    fontWeight: 700,
                    padding: '4px 12px',
                    borderRadius: 99,
                  }}
                >
                  {c.category || 'S/C'}
                </div>
                <span style={{ fontSize: 24, fontWeight: 600, color: textPrimary }}>
                  {formatDistance(c.length)}
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                <span style={{ fontSize: 24, fontWeight: 700, color: '#276749' }}>+{c.gain} m</span>
                <span style={{ fontSize: 15, color: textDim }}>{formatGrade(c.avgGrade)} med.</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Footer ────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: 'auto' }}>
        <div style={{ fontSize: format === 'story' ? 22 : 18, color: textDim }}>
          routeforecast.app
        </div>
        {depLabel && (
          <div style={{ fontSize: format === 'story' ? 18 : 15, color: textDim }}>
            {depLabel}
          </div>
        )}
      </div>
    </div>
  );
});

export default ShareCard;
